import { ComponentType, ReactNode, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { ColumnHeaderPaper } from '@rossum/components/data-grid/ColumnHeaderPaper';
import {
  clearedCellOutlineStyles,
  transparentBorderStyles,
} from '@rossum/components/data-grid/styles';
import { Filters } from '@rossum/components/filtering';
import { HeaderProps } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Box, LinearProgress, Stack } from '@rossum/ui/material';
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridEventListener,
  GridPaginationModel,
  GridRowParams,
  useGridApiRef,
} from '@rossum/ui/x-data-grid-pro';
import { ExtensionLogsGuard } from '../../../../../components/Restrictors';
import { pageSizeOptions } from '../../../../../constants/values';
import { useDataGridLocaleText } from '../../../../../i18n/useDataGridLocaleText';
import { ACTIVITIES_QUERY } from '../../../../../redux/modules/localStorage/actions';
import { HelmetComponent } from '../../../../../routes/HelmetComponent';
import useDebounce from '../../../../../utils/hooks/useDebounce';
import { useStoredQuery } from '../../../../../utils/hooks/useStoredQuery';
import { useRowCount } from '../../../../document-list-base/hooks/usePagination';
import { absoluteMaxSizeStyles } from '../../../../document-upload/components/helpers';
import { useExtensionLogsFeatureSubscription } from '../../../../pricing/hooks/useExtensionsFeatureSubscription';
import { useWorkspacesWithQueues } from '../../../../queues/hooks/useWorkspacesWithQueues';
import { notify } from '../../../../toaster';
import { useUnpaginatedHooks } from '../../../hooks/useUnpaginatedHooks';
import {
  activitiesDataGridStyles,
  getTransformedActivities,
  TransformedActivity,
} from '../helpers';
import {
  activitiesQueryParser,
  useActivitiesFilterSettings,
} from '../hooks/useActivitiesFilterSettings';
import { useColumns } from '../hooks/useColumns';
import { useColumnWidths } from '../hooks/useColumnWidths';
import {
  QUERY_KEY_NEW_ACTIVITIES_CHECK,
  useNewActivitiesCheck,
} from '../hooks/useNewActivitiesCheck';
import { useRuns } from '../hooks/useRuns';
import {
  QUERY_KEY_RUNS_STATUS_POLL,
  useRunsStatusPolling,
} from '../hooks/useRunsStatusPolling';
import { ActivitiesHeader } from './ActivitiesHeader';
import { PanelContent } from './activity-row/PanelContent';
import { Row, ROW_HEIGHT, ROW_SPACING } from './activity-row/Row';
import { LoadingSkeletons } from './EmptyStates';

const NEW_ACTIVITIES_TOAST_ID = 'new-activities-available';

export type ActivitiesHeaderProps = Pick<HeaderProps, 'scrollableDivRef'> & {
  title: string;
  description: string;
  searchValue: string;
  setSearchValue: (value: string) => void;
  refreshRuns: () => void;
  isRefetching: boolean;
  dataUpdatedAt: number;
};

type ActivitiesPageProps = {
  HeaderComponent?: ComponentType<ActivitiesHeaderProps>;
  alert?: ReactNode;
};

export const ActivitiesPage = ({
  HeaderComponent = ActivitiesHeader,
  alert,
}: ActivitiesPageProps) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();

  const [parsedParams, updateParsedParams] = useStoredQuery({
    parser: activitiesQueryParser,
    localStorageKey: ACTIVITIES_QUERY,
  });

  const {
    data: extensions,
    isLoading: isLoadingExtensions,
    refetch: refetchExtensions,
  } = useUnpaginatedHooks({ pageSize: 100 });

  const {
    queues,
    workspaces,
    isLoading: isQueuesLoading,
  } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const filterSettings = useActivitiesFilterSettings({
    extensions,
    isLoadingExtensions,
    queues,
    workspaces,
    isQueuesLoading,
    setFilters: updateParsedParams,
  });

  const localeText = useDataGridLocaleText();

  // Debounce search because it's likely to be rapidly changing
  const debouncedSearch = useDebounce(parsedParams.search, 250);

  // Base query without pagination - used for new activities check
  const { page, pageSize, ...activitiesQuery } = {
    ...parsedParams,
    search: debouncedSearch,
  };

  const logsSubscription = useExtensionLogsFeatureSubscription();

  const {
    data: runs,
    refetch: refetchRuns,
    isLoading,
    isError,
    isRefetching,
    dataUpdatedAt,
  } = useRuns(
    {
      ...activitiesQuery,
      page: page ?? 1,
      pageSize: pageSize ?? 100,
    },
    {
      enabled: logsSubscription.canAccess,
    }
  );

  const handleRefreshRuns = useCallback(() => {
    // Cancel in-flight polling queries to prevent stale data from being merged
    queryClient.cancelQueries({ queryKey: [QUERY_KEY_RUNS_STATUS_POLL] });
    queryClient.cancelQueries({ queryKey: [QUERY_KEY_NEW_ACTIVITIES_CHECK] });

    // Dismiss the new activities toast since we're syncing
    toast.dismiss(NEW_ACTIVITIES_TOAST_ID);

    refetchRuns();
    refetchExtensions();
  }, [queryClient, refetchRuns, refetchExtensions]);

  // Poll only running jobs for status updates
  useRunsStatusPolling({
    runs: runs?.results ?? [],
    enabled: !isLoading,
  });

  // Check for new activities - only when on page 1, since comparing
  // the first item of page 2+ with page 1 would always show differences
  const latestKnownUuid = runs?.results?.[0]?.uuid;
  const isOnFirstPage = (page ?? 1) === 1;
  useNewActivitiesCheck({
    latestKnownUuid,
    activitiesQuery,
    enabled: !isLoading && isOnFirstPage,
    onNewActivities: () => {
      notify.default(
        {
          title: intl.formatMessage({
            id: 'features.activities.newActivitiesAvailable',
          }),
          id: NEW_ACTIVITIES_TOAST_ID,
          toastActions: [
            {
              label: intl.formatMessage({
                id: 'features.activities.header.refresh',
              }),
              onClick: handleRefreshRuns,
            },
          ],
        },
        { duration: Infinity }
      );
    },
  });

  // Dismiss the new activities toast when leaving the page or navigating away from page 1
  useEffect(() => {
    if (!isOnFirstPage) {
      toast.dismiss(NEW_ACTIVITIES_TOAST_ID);
    }
    return () => {
      toast.dismiss(NEW_ACTIVITIES_TOAST_ID);
    };
  }, [isOnFirstPage]);

  const activities = getTransformedActivities(runs?.results ?? []);

  const { columnWidths, setColumnWidths } = useColumnWidths();

  const { columns } = useColumns({
    extensions,
    isLoadingExtensions,
    columnWidths,
  });

  const rowCount = useRowCount(runs?.pagination?.total);

  const paginationModel = {
    page: (page ?? 1) - 1,
    pageSize: pageSize ?? 100,
  };

  const handleOnPaginationModelChange = useCallback(
    (newPaginationModel: GridPaginationModel) =>
      updateParsedParams({
        page: newPaginationModel.page + 1,
        pageSize: newPaginationModel.pageSize,
      }),
    [updateParsedParams]
  );

  // functions getDetailPanelContent, getDetailPanelHeight and onRowClick have to be memoized
  // The Data Grid depends on the referential value of these props to cache their values and optimize the rendering.
  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const getDetailPanelContent = useCallback(
    (props: GridRowParams<TransformedActivity>) => <PanelContent {...props} />,
    []
  );

  const onRowClick = useCallback<GridEventListener<'rowClick'>>(
    params => {
      apiRef.current?.toggleDetailPanel(params.id);
    },
    [apiRef]
  );

  return (
    <>
      <HelmetComponent translationKey="features.routes.pageTitles.extensions.activities" />
      <PageLayout
        fullWidth
        renderHeader={({ scrollableDivRef }) => (
          <HeaderComponent
            scrollableDivRef={scrollableDivRef}
            title={intl.formatMessage({
              id: 'features.activities.extensions.title',
            })}
            description={intl.formatMessage({
              id: 'features.activities.extensions.description',
            })}
            refreshRuns={handleRefreshRuns}
            searchValue={parsedParams.search ?? ''}
            setSearchValue={(search: string) => {
              updateParsedParams({
                search: search.length > 0 ? search : undefined,
              });
            }}
            isRefetching={isRefetching}
            dataUpdatedAt={dataUpdatedAt}
          />
        )}
      >
        <ExtensionLogsGuard>
          <Box sx={{ position: 'absolute', width: '100%' }}>
            {isRefetching ? <LinearProgress /> : null}
          </Box>
          <Stack p={4} flex={1} spacing={2}>
            {alert}
            <Filters
              dataCy="activities-filters"
              filters={parsedParams}
              setFilters={updateParsedParams}
              filterSettings={filterSettings}
            />
            <Box position="relative" flexGrow={1} height={1}>
              <DataGridPro
                disableRowSelectionExcludeModel
                apiRef={apiRef}
                loading={isLoading}
                columns={columns}
                rows={activities}
                localeText={{
                  ...localeText,
                  noRowsLabel: intl.formatMessage({
                    id: isError
                      ? 'features.activities.rows.error.title'
                      : 'features.activities.rows.empty',
                  }),
                  noResultsOverlayLabel: intl.formatMessage({
                    id: isError
                      ? 'features.activities.rows.error.title'
                      : 'features.activities.rows.empty',
                  }),
                }}
                disableRowSelectionOnClick
                disableColumnMenu
                disableColumnReorder
                disableColumnFilter
                pagination
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={handleOnPaginationModelChange}
                pageSizeOptions={pageSizeOptions}
                rowHeight={ROW_HEIGHT + ROW_SPACING}
                onRowClick={onRowClick}
                getDetailPanelHeight={getDetailPanelHeight}
                getDetailPanelContent={getDetailPanelContent}
                onColumnWidthChange={setColumnWidths}
                initialState={{
                  pinnedColumns: {
                    right: [GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field],
                  },
                }}
                slots={{
                  loadingOverlay: () => <LoadingSkeletons />,
                  row: Row,
                  columnHeaders: ColumnHeaderPaper,
                }}
                slotProps={{
                  noRowsOverlay: {
                    description: isError
                      ? intl.formatMessage({
                          id: 'features.activities.rows.error.text',
                        })
                      : undefined,
                  },
                  noResultsOverlay: {
                    description: isError
                      ? intl.formatMessage({
                          id: 'features.activities.rows.error.text',
                        })
                      : undefined,
                  },
                  row: { 'data-cy': 'activity-row' },
                  basePagination: {
                    // @ts-expect-error wrong type
                    labelRowsPerPage: intl.formatMessage({
                      id: 'containers.allDocuments.pagination.label',
                    }),
                    'data-cy': 'activities-pagination',
                    sx: {
                      display: rowCount === 0 ? 'none' : 'inherit',
                      justifyContent: 'flex-end',
                      [`> * p`]: {
                        margin: 0,
                      },
                    },
                  },
                  baseSwitch: {
                    // @ts-expect-error
                    color: 'secondary',
                  },
                }}
                sx={{
                  ...clearedCellOutlineStyles,
                  ...transparentBorderStyles,
                  ...absoluteMaxSizeStyles,
                  ...activitiesDataGridStyles,
                }}
              />
            </Box>
          </Stack>
        </ExtensionLogsGuard>
      </PageLayout>
    </>
  );
};
